import type { LocationId, BackgroundType } from '@/types';

export interface Location {
  id: LocationId;
  nombre: string;
  descripcion: string;
  background: BackgroundType;
  /** Posición en el mapa ilustrativo (0-100 en ambos ejes). */
  posicion: { x: number; y: number };
  /** Otras locaciones directamente accesibles desde esta. */
  conexiones: LocationId[];
  /** Si está oculta del mapa hasta que el jugador la descubra. */
  oculta?: boolean;
}

export const LOCACIONES: Record<LocationId, Location> = {
  hogar_ruinas: {
    id: 'hogar_ruinas',
    nombre: 'Hogar en Ruinas',
    descripcion:
      'Las vigas caídas todavía humean. Esto fue tu casa. De acá arrancó todo.',
    background: 'ruins',
    posicion: { x: 18, y: 72 },
    conexiones: ['bosque_susurrante', 'tierras_quemadas'],
  },
  bosque_susurrante: {
    id: 'bosque_susurrante',
    nombre: 'Bosque Susurrante',
    descripcion:
      'El bosque que siempre estuvo al lado de tu cabaña. Ahora sabés que te escuchaba.',
    background: 'forest',
    posicion: { x: 35, y: 60 },
    conexiones: [
      'hogar_ruinas',
      'villa_capibarica',
      'rio_ancestral',
      'santuario',
    ],
  },
  villa_capibarica: {
    id: 'villa_capibarica',
    nombre: 'Villa Capibárica',
    descripcion: 'Chozas de caña y madera. Los capibaras del Clan del Río conviven acá.',
    background: 'village',
    posicion: { x: 48, y: 48 },
    conexiones: ['bosque_susurrante', 'rio_ancestral', 'laguna_sagrada'],
  },
  rio_ancestral: {
    id: 'rio_ancestral',
    nombre: 'Río Ancestral',
    descripcion: 'Aguas profundas, frontera entre lo conocido y lo mítico.',
    background: 'river',
    posicion: { x: 42, y: 70 },
    conexiones: ['bosque_susurrante', 'villa_capibarica', 'laguna_sagrada'],
  },
  acantilados_olvidados: {
    id: 'acantilados_olvidados',
    nombre: 'Acantilados Olvidados',
    descripcion:
      'Donde el viento te cuenta cosas que nadie más oye. Territorio de los Susurradores.',
    background: 'cliffs',
    posicion: { x: 75, y: 30 },
    conexiones: ['santuario', 'corazon_civilizacion'],
    oculta: true,
  },
  santuario: {
    id: 'santuario',
    nombre: 'El Santuario',
    descripcion: 'Un templo abandonado en el centro del bosque. Nadie recuerda quién lo construyó.',
    background: 'mystical',
    posicion: { x: 58, y: 32 },
    conexiones: [
      'bosque_susurrante',
      'acantilados_olvidados',
      'corazon_civilizacion',
      'guarida_sombra',
    ],
    oculta: true,
  },
  guarida_sombra: {
    id: 'guarida_sombra',
    nombre: 'Guarida de la Sombra',
    descripcion: 'Cuevas húmedas con cristales que emiten luz tenue. No entres solo.',
    background: 'cave',
    posicion: { x: 72, y: 55 },
    conexiones: ['santuario', 'tierras_quemadas'],
    oculta: true,
  },
  laguna_sagrada: {
    id: 'laguna_sagrada',
    nombre: 'Laguna Sagrada',
    descripcion:
      'Espejo de agua bajo la luna. Las ceremonias más viejas se hacen acá.',
    background: 'mystical',
    posicion: { x: 52, y: 62 },
    conexiones: ['villa_capibarica', 'rio_ancestral'],
    oculta: true,
  },
  tierras_quemadas: {
    id: 'tierras_quemadas',
    nombre: 'Tierras Quemadas',
    descripcion: 'Zona devastada por los incendios. Donde empezás a entender quién los provoca.',
    background: 'fire',
    posicion: { x: 25, y: 82 },
    conexiones: ['hogar_ruinas', 'guarida_sombra'],
    oculta: true,
  },
  corazon_civilizacion: {
    id: 'corazon_civilizacion',
    nombre: 'Corazón de la Civilización',
    descripcion:
      'Ciudad oculta capibárica, ruinas del Gran Guardián original. Acá se resuelve todo.',
    background: 'ruins',
    posicion: { x: 85, y: 15 },
    conexiones: ['santuario', 'acantilados_olvidados'],
    oculta: true,
  },
};

export function obtenerLocacion(id: LocationId): Location {
  return LOCACIONES[id];
}

export function todasLasLocaciones(): Location[] {
  return Object.values(LOCACIONES);
}
